import { useState, useEffect } from 'react';
import axios from 'axios';
import { Brain, Users, User, MessageSquare, RefreshCw, Send, Clock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const QUICK_QUESTIONS = [
  "Donne-moi un état des lieux complet pour Qualiopi",
  "Quels stagiaires ont besoin d'un accompagnement renforcé ?",
  "Résume les absences et retards de cette période",
  "Quelles sont les compétences les moins bien maîtrisées ?"
];

const formatAnalyse = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('**') && line.includes('**')) {
      const title = line.replace(/\*\*/g, '');
      return <h3 key={i} className="font-bold text-[#0F172A] mt-4 mb-1 text-sm border-b border-slate-200 pb-1">{title}</h3>;
    }
    if (line.startsWith('- ') || line.startsWith('• ')) {
      return <li key={i} className="text-sm text-slate-700 ml-4 mb-1">{line.slice(2)}</li>;
    }
    if (line.trim() === '') return <br key={i} />;
    return <p key={i} className="text-sm text-slate-700 mb-1">{line}</p>;
  });
};

const TabButton = ({ active, onClick, icon: Icon, label, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
      active
        ? 'bg-white text-[#0F172A] border border-b-0 border-slate-200 -mb-px'
        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
    }`}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

const IAAnalyticsCentre = ({ selectedCentre, stagiaires }) => {
  const [activeTab, setActiveTab] = useState('collectif');
  const [bilanCentre, setBilanCentre] = useState('');
  const [bilanData, setBilanData] = useState(null);
  const [bilanLoading, setBilanLoading] = useState(false);
  const [selectedStagiaire, setSelectedStagiaire] = useState('');
  const [stagiaireAnalyse, setStagiaireAnalyse] = useState(null);
  const [stagiaireLoading, setStagiaireLoading] = useState(false);
  const [questionData, setQuestionData] = useState(null);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [questionText, setQuestionText] = useState('');

  // Extraire les centres uniques des stagiaires
  const centres = [...new Set(stagiaires.map(s => s.centre_formation).filter(Boolean))];

  useEffect(() => {
    if (centres.length > 0 && !bilanCentre) {
      setBilanCentre(centres[0]);
    }
  }, [centres.length]);

  useEffect(() => {
    if (bilanCentre) {
      setBilanData(null);
      loadBilan();
    }
  }, [bilanCentre]);

  useEffect(() => {
    loadLastQuestion();
  }, [selectedCentre]);

  useEffect(() => {
    if (selectedStagiaire) loadStagiaireAnalyse(selectedStagiaire);
  }, [selectedStagiaire]);

  const loadBilan = async () => {
    try {
      const res = await axios.get('/ai-centre/bilan', { params: { centre: bilanCentre } });
      if (res.data.analyse) setBilanData(res.data);
    } catch { /* no cache */ }
  };

  const generateBilan = async () => {
    setBilanLoading(true);
    try {
      const res = await axios.post(`/ai-centre/bilan?centre=${bilanCentre}`);
      setBilanData(res.data);
      toast.success(`Bilan du centre ${bilanCentre} généré`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur';
      toast.error(detail);
    } finally {
      setBilanLoading(false);
    }
  };

  const loadStagiaireAnalyse = async (stagId) => {
    setStagiaireAnalyse(null);
    setStagiaireLoading(true);
    try {
      const res = await axios.get(`/analyse-competences/${stagId}`);
      if (res.data.analyse) setStagiaireAnalyse(res.data);
      else setStagiaireAnalyse({ analyse: null });
    } catch {
      setStagiaireAnalyse({ analyse: null });
    } finally {
      setStagiaireLoading(false);
    }
  };

  const loadLastQuestion = async () => {
    try {
      const res = await axios.get('/ai-centre/question', { params: { centre: selectedCentre } });
      if (res.data.reponse) setQuestionData(res.data);
    } catch { /* no cache */ }
  };

  const submitQuestion = async (q) => {
    const text = q || questionText;
    if (!text || text.trim().length < 5) {
      toast.error('Saisissez une question (min 5 caractères)');
      return;
    }
    setQuestionLoading(true);
    try {
      const res = await axios.post('/ai-centre/question', { question: text.trim(), centre: selectedCentre });
      setQuestionData(res.data);
      setQuestionText('');
      toast.success('Réponse générée');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur';
      toast.error(detail);
    } finally {
      setQuestionLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="ia-centre-section">
      {/* Header */}
      <div className="p-6 pb-0">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg">
            <Brain className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-display font-semibold text-[#0F172A]">Assistant IA du Centre</h2>
            <p className="text-xs text-slate-500">Analyse intelligente de toutes les données de formation (1 analyse/semaine)</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200">
          <TabButton active={activeTab === 'collectif'} onClick={() => setActiveTab('collectif')} icon={Users} label="Bilan collectif" testId="tab-collectif" />
          <TabButton active={activeTab === 'individuel'} onClick={() => setActiveTab('individuel')} icon={User} label="Analyse individuelle" testId="tab-individuel" />
          <TabButton active={activeTab === 'question'} onClick={() => setActiveTab('question')} icon={MessageSquare} label="Question libre" testId="tab-question" />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* TAB 1: Bilan collectif */}
        {activeTab === 'collectif' && (
          <div data-testid="tab-content-collectif">
            {/* Sélecteur de centre */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Centre à analyser</label>
              <select
                value={bilanCentre}
                onChange={(e) => setBilanCentre(e.target.value)}
                data-testid="select-centre-bilan"
                className="w-full max-w-xs h-10 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                {centres.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {bilanData?.analyse ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Clock className="h-3 w-3" />
                    <span>Bilan {bilanCentre} — Généré le {formatDate(bilanData.created_at)}</span>
                    {bilanData.cached && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-xs">cache</span>}
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 max-h-[500px] overflow-y-auto">
                  {formatAnalyse(bilanData.analyse)}
                </div>
              </div>
            ) : (
              <div className="text-center py-10">
                <Sparkles className="h-12 w-12 mx-auto mb-3 text-violet-400" />
                <p className="text-slate-700 font-medium mb-1">Bilan hebdomadaire — {bilanCentre}</p>
                <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
                  L'IA analyse toutes les données du centre {bilanCentre} (notes, compétences, présences, commentaires) et produit un bilan complet.
                </p>
                <Button
                  onClick={generateBilan}
                  disabled={bilanLoading || !bilanCentre}
                  data-testid="generate-bilan-btn"
                  className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
                >
                  {bilanLoading ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Analyse en cours...</>
                  ) : (
                    <><Brain className="h-4 w-4 mr-2" />Générer le bilan de {bilanCentre}</>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: Analyse individuelle */}
        {activeTab === 'individuel' && (
          <div data-testid="tab-content-individuel">
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Sélectionner un stagiaire</label>
              <select
                value={selectedStagiaire}
                onChange={(e) => setSelectedStagiaire(e.target.value)}
                data-testid="select-stagiaire"
                className="w-full max-w-md h-10 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">-- Choisir un stagiaire --</option>
                {stagiaires.map(s => (
                  <option key={s.user_id} value={s.user_id}>
                    {s.user_info?.prenom} {s.user_info?.nom} {s.numero_candidat ? `(${s.numero_candidat})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {stagiaireLoading && (
              <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="text-sm">Chargement...</span>
              </div>
            )}

            {!stagiaireLoading && selectedStagiaire && stagiaireAnalyse && (
              stagiaireAnalyse.analyse ? (
                <div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <Clock className="h-3 w-3" />
                    <span>Généré le {formatDate(stagiaireAnalyse.created_at)}</span>
                    {stagiaireAnalyse.cached && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-xs">cache</span>}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 max-h-[500px] overflow-y-auto">
                    {formatAnalyse(stagiaireAnalyse.analyse)}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <p className="text-sm">Aucune analyse disponible pour ce stagiaire.</p>
                  <p className="text-xs mt-1">L'analyse peut être générée depuis l'espace Progression du stagiaire.</p>
                </div>
              )
            )}

            {!selectedStagiaire && !stagiaireLoading && (
              <div className="text-center py-8 text-slate-400">
                <User className="h-10 w-10 mx-auto mb-2" />
                <p className="text-sm">Sélectionnez un stagiaire pour voir son analyse IA</p>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: Question libre */}
        {activeTab === 'question' && (
          <div data-testid="tab-content-question">
            {/* Quick suggestions */}
            <div className="mb-4">
              <p className="text-xs text-slate-500 mb-2">Suggestions :</p>
              <div className="flex flex-wrap gap-2">
                {QUICK_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setQuestionText(q)}
                    data-testid={`quick-question-${i}`}
                    className="px-3 py-1.5 text-xs bg-violet-50 text-violet-700 rounded-full border border-violet-200 hover:bg-violet-100 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Input */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitQuestion()}
                placeholder="Posez votre question au centre..."
                data-testid="question-input"
                className="flex-1 h-10 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
              <Button
                onClick={() => submitQuestion()}
                disabled={questionLoading || !questionText.trim()}
                data-testid="submit-question-btn"
                className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
              >
                {questionLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Last response */}
            {questionData?.reponse ? (
              <div>
                <div className="bg-violet-50 rounded-lg p-3 mb-3 border border-violet-200">
                  <p className="text-xs text-violet-600 font-medium mb-1">Question posée :</p>
                  <p className="text-sm text-violet-800">{questionData.question}</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <Clock className="h-3 w-3" />
                  <span>Répondu le {formatDate(questionData.created_at)}</span>
                  {questionData.cached && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-xs">cache</span>}
                </div>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 max-h-[400px] overflow-y-auto">
                  {formatAnalyse(questionData.reponse)}
                </div>
              </div>
            ) : (
              !questionLoading && (
                <div className="text-center py-6 text-slate-400">
                  <MessageSquare className="h-10 w-10 mx-auto mb-2" />
                  <p className="text-sm">Posez votre première question</p>
                  <p className="text-xs mt-1">L'IA analysera toutes les données du centre pour y répondre</p>
                </div>
              )
            )}

            {questionLoading && (
              <div className="flex items-center gap-3 justify-center py-8">
                <RefreshCw className="h-5 w-5 animate-spin text-violet-500" />
                <span className="text-sm text-slate-600">L'IA analyse les données du centre...</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IAAnalyticsCentre;
